'use client'

import { useState } from 'react'
import { useTranslations } from 'next-intl'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, CheckCircle2 } from 'lucide-react'

const formSchema = z.object({
  name_en: z.string().min(1, 'Agency name is required').max(200),
  name_ar: z.string().max(200).optional(),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().max(30).optional(),
  contact_person: z.string().max(200).optional(),
  license_number: z.string().max(100).optional(),
})

interface AffiliateSignupFormProps {
  tenantId: string
}

export function AffiliateSignupForm({ tenantId }: AffiliateSignupFormProps) {
  const t = useTranslations('booking.affiliate')
  const [formData, setFormData] = useState({
    name_en: '',
    name_ar: '',
    email: '',
    phone: '',
    contact_person: '',
    license_number: '',
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    // Validate
    const result = formSchema.safeParse(formData)
    if (!result.success) {
      const errors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        if (!errors[field]) errors[field] = issue.message
      }
      setFieldErrors(errors)
      return
    }

    setSubmitting(true)
    try {
      const response = await fetch('/api/agencies/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenantId,
          name_en: formData.name_en,
          name_ar: formData.name_ar || undefined,
          email: formData.email,
          phone: formData.phone || undefined,
          contact_person: formData.contact_person || undefined,
          license_number: formData.license_number || undefined,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || 'Registration failed. Please try again.')
        return
      }

      setSubmitted(true)
    } catch {
      setError('Something went wrong. Please try again later.')
    } finally {
      setSubmitting(false)
    }
  }

  // Success state
  if (submitted) {
    return (
      <div className="text-center py-6">
        <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {t('applicationSubmitted')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('thankYou', { email: formData.email })}
        </p>
      </div>
    )
  }

  const updateField = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }))
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="name_en">{t('agencyNameEn')}</Label>
        <Input
          id="name_en"
          value={formData.name_en}
          onChange={updateField('name_en')}
          placeholder={t('placeholders.agencyName')}
        />
        {fieldErrors.name_en && (
          <p className="text-xs text-red-600 dark:text-red-400">{fieldErrors.name_en}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="name_ar">{t('agencyNameAr')}</Label>
        <Input
          id="name_ar"
          value={formData.name_ar}
          onChange={updateField('name_ar')}
          dir="rtl"
          placeholder="اسم الوكالة"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="email">{t('emailLabel')}</Label>
        <Input
          id="email"
          type="email"
          value={formData.email}
          onChange={updateField('email')}
          placeholder={t('placeholders.email')}
        />
        {fieldErrors.email && (
          <p className="text-xs text-red-600 dark:text-red-400">{fieldErrors.email}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="phone">{t('phoneLabel')}</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={updateField('phone')}
            placeholder={t('placeholders.phone')}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="contact_person">{t('contactPerson')}</Label>
          <Input
            id="contact_person"
            value={formData.contact_person}
            onChange={updateField('contact_person')}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="license_number">{t('licenseNumber')}</Label>
        <Input
          id="license_number"
          value={formData.license_number}
          onChange={updateField('license_number')}
          placeholder={t('optional')}
        />
      </div>

      <Button type="submit" className="w-full" disabled={submitting}>
        {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
        {t('submitApplication')}
      </Button>

      <p className="text-xs text-center text-gray-500 dark:text-gray-400">
        {t('bySubmitting')}
      </p>
    </form>
  )
}
